import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
    Chip,
    Alert,
    CircularProgress,
    Card,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { v4 as uuid } from 'uuid';
import { Item, FruitType } from '../types';
import { DateOnlyPicker } from './DateOnlyPicker';

interface ItemsDialogProps {
    open: boolean;
    customerName: string;
    customerPhone: string;
    customerAddress: string;
    customerType?: string;
    purchaseNumber?: string;
    currentDate: string;
    fruitTypes: Record<string, FruitType>;
    initialItems?: Item[];
    saveError?: string;
    saving?: boolean;
    onClose: () => void;
    onSave: (items: Item[], date: string) => Promise<boolean>;
    onUpsertFruitType: (fruit: FruitType) => Promise<void> | void;
}

export const ItemsDialog: React.FC<ItemsDialogProps> = ({
    open,
    customerName,
    customerPhone,
    customerAddress,
    customerType = 'Retail',
    purchaseNumber,
    fruitTypes,
    currentDate,
    initialItems,
    saveError,
    saving = false,
    onClose,
    onSave,
    onUpsertFruitType,
}) => {
    const [date, setDate] = React.useState(currentDate);
    const [barcode, setBarcode] = React.useState('');
    const [fruitName, setFruitName] = React.useState('');
    const [price, setPrice] = React.useState('');
    const [weight, setWeight] = React.useState('');
    const [items, setItems] = React.useState<Item[]>([]);
    const [error, setError] = React.useState('');
    const [addFruitOpen, setAddFruitOpen] = React.useState(false);
    const [newFruitId, setNewFruitId] = React.useState('');
    const [newFruitName, setNewFruitName] = React.useState('');
    const [newFruitPrice, setNewFruitPrice] = React.useState('');
    const [newFruitWholesalePrice, setNewFruitWholesalePrice] = React.useState('');
    const [addFruitError, setAddFruitError] = React.useState('');
    const [addFruitSaving, setAddFruitSaving] = React.useState(false);

    const parseWeightInput = (input: string) => {
        const trimmed = input.trim();
        if (!trimmed) {
            return { total: 0, details: '' };
        }
        const parts = trimmed.split('+').map(part => part.trim()).filter(part => part.length > 0);
        const numbers = parts.map(part => Number(part));
        const hasInvalid = numbers.some(num => Number.isNaN(num));
        if (hasInvalid) {
            return { total: NaN, details: trimmed };
        }
        const total = numbers.reduce((sum, num) => sum + num, 0);
        return { total, details: trimmed };
    };

    const ensureDetails = (item: Item): Item => ({
        ...item,
        details: item.details,
        total: item.price * item.weight,
    });

    const aggregateItems = (list: Item[]) => {
        const grouped: Record<string, Item> = {};

        list.forEach(rawItem => {
            const item = ensureDetails(rawItem);
            const key = `${item.fruitTypeId}-${item.price}`;
            const existing = grouped[key];

            if (existing) {
                const combinedWeight = existing.weight + item.weight;
                grouped[key] = {
                    ...existing,
                    weight: combinedWeight,
                    total: item.price * combinedWeight,
                    details: (item.details ?? item.weight.toString()) + ' + ' + (existing.details ?? existing.weight.toString()),
                };
            } else {
                grouped[key] = item;
            }
        });

        return Object.values(grouped);
    };

    const barcodeInputRef = React.useRef<HTMLInputElement>(null);
    const weightInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (open) {
            setDate(currentDate);
            setItems((initialItems ?? []).map(ensureDetails));
            setBarcode('');
            setFruitName('');
            setPrice('');
            setWeight('');
            setError('');

            if (barcodeInputRef.current) {
                setTimeout(() => barcodeInputRef.current?.focus(), 100);
            }
            setTimeout(() => {
                barcodeInputRef.current?.focus();
            }, 100);
        }
    }, [open, initialItems]);

    const handleBarcodeChange = (value: string) => {
        setBarcode(value);
        setError('');

        // Check if fruit type exists
        if (fruitTypes[value]) {
            const fruit = fruitTypes[value];
            setFruitName(fruit.name);
            // Use wholesale price if customer is wholesale, otherwise use retail price
            const displayPrice = customerType === 'Wholesale' && fruit.wholesalePrice
                ? fruit.wholesalePrice
                : fruit.price;
            setPrice(displayPrice.toString());
        } else {
            setFruitName('');
            setPrice('');
        }
    };

    // When customer type changes, update price for current barcode
    React.useEffect(() => {
        if (!barcode || !fruitTypes[barcode]) return;
        const fruit = fruitTypes[barcode];
        const displayPrice = customerType === 'Wholesale' && fruit.wholesalePrice
            ? fruit.wholesalePrice
            : fruit.price;
        setPrice(displayPrice.toString());
    }, [customerType, barcode, fruitTypes]);

    const handleAddItem = async (clearBarcode: boolean) => {
        const trimmedWeight = weight.trim();
        const parsedWeight = parseWeightInput(trimmedWeight);

        // Validation
        if (!barcode.trim()) {
            setError('Vui lòng nhập mã vạch');
            return;
        }
        if (!fruitName.trim()) {
            setError('Vui lòng nhập tên loại trái cây');
            return;
        }
        if (!price || parseInt(price, 10) <= 0) {
            setError('Vui lòng nhập giá hợp lệ (số nguyên)');
            return;
        }
        if (trimmedWeight !== '' && Number.isNaN(parsedWeight.total)) {
            setError('Vui lòng nhập số lượng hợp lệ (số thập phân)');
            return;
        }
        if (trimmedWeight !== '' && parsedWeight.total < 0) {
            setError('Vui lòng nhập số lượng hợp lệ (số thập phân)');
            return;
        }
        if (trimmedWeight !== '' && parsedWeight.total !== 0) {
            const priceNum = parseInt(price, 10);
            const weightNum = parsedWeight.total;
            const total = priceNum * weightNum;

            // Create new item
            const newItem: Item = {
                id: uuid(),
                fruitTypeId: barcode,
                fruitTypeName: fruitName,
                price: priceNum,
                weight: weightNum,
                total: total,
                details: parsedWeight.details || weightNum.toString(),
            };

            setItems([newItem, ...items.map(ensureDetails)]);

            // Save/update fruit type via API handler
            await onUpsertFruitType({
                id: barcode,
                name: fruitName,
                price: customerType === 'Wholesale' ? fruitTypes[barcode]?.price || priceNum : priceNum,
                wholesalePrice: customerType === 'Wholesale' ? priceNum : fruitTypes[barcode]?.wholesalePrice || priceNum,
                lastUpdated: new Date().toISOString(),
            });
        }
        if (clearBarcode) {
            setItems(prev => aggregateItems(prev));
            setBarcode('');
            setFruitName('');
            setPrice('');
            setWeight('');
            setError('');
            setTimeout(() => barcodeInputRef.current?.focus(), 100);
        }
        else {
            setWeight('');
            setError('');
        }

        // Focus back to barcode input
        // setTimeout(() => barcodeInputRef.current?.focus(), 100);
    };

    const handleKeyPress = (e: React.KeyboardEvent, field: string) => {
        if (e.key === 'Enter') {
            e.preventDefault();

            if (field === 'barcode' && barcode) {
                // Nếu chưa có tên trái cây thì focus vào ô tên, ngược lại chuyển thẳng sang số lượng
                if (!fruitName) {
                    if (barcode == "0")
                        handleSave();
                    else
                        document.getElementById('fruit-name-input')?.focus();
                } else {
                    document.getElementById('weight-input')?.focus();
                }
            } else if (field === 'name' && fruitName) {
                document.getElementById('price-input')?.focus();
            } else if (field === 'price' && price) {
                document.getElementById('weight-input')?.focus();
            } else if (field === 'weight') {
                handleAddItem(true);
            }
        }
        // else if (e.key === '+') {
        //     e.preventDefault();
        //     if (field === 'weight') {
        //         handleAddItem(false);
        //     }
        // }
    };

    const handleRemoveItem = (id: string) => {
        setItems(items.filter(item => item.id !== id));
    };

    const handleSubmitAddFruit = async () => {
        if (!newFruitId.trim()) {
            setAddFruitError('Vui lòng nhập mã vạch');
            return;
        }
        if (!newFruitName.trim()) {
            setAddFruitError('Vui lòng nhập tên trái cây');
            return;
        }
        if (!newFruitPrice.trim() || Number(newFruitPrice) <= 0) {
            setAddFruitError('Vui lòng nhập giá hợp lệ (>0)');
            return;
        }
        const isEditing = fruitTypes[newFruitId];
        if (!isEditing && fruitTypes[newFruitId]) {
            setAddFruitError('Mã vạch đã tồn tại');
            return;
        }

        setAddFruitSaving(true);
        setAddFruitError('');
        try {
            const payload: FruitType = {
                id: newFruitId.trim(),
                name: newFruitName.trim(),
                price: Math.trunc(Number(newFruitPrice)),
                wholesalePrice: Math.trunc(Number(newFruitWholesalePrice) || 0),
                lastUpdated: new Date().toISOString(),
            };
            await onUpsertFruitType(payload);
            setAddFruitOpen(false);
            handleBarcodeChange(payload.id);
            setPrice(payload.price.toString());
            setFruitName(payload.name);
            setBarcode(payload.id);
            setTimeout(() => barcodeInputRef.current?.focus(), 100);
        } catch (err: any) {
            setAddFruitError(err.message || 'Lỗi lưu loại trái cây');
        } finally {
            setAddFruitSaving(false);
        }
    };

    const handleCloseAddFruit = () => {
        setAddFruitOpen(false);
        setAddFruitError('');
    };

    const handleStartAddFruit = () => {
        setAddFruitOpen(true);
        setNewFruitId('');
        setNewFruitName('');
        setNewFruitPrice('');
        setNewFruitWholesalePrice('');
        setAddFruitError('');
    };

    const handleSave = () => {
        if (items.length === 0) {
            setError('Vui lòng thêm ít nhất một loại trái cây');
            return;
        }

        onSave(items, date).then(success => {
            if (success) {
                // Reset
                setItems([]);
                setBarcode('');
                setFruitName('');
                setPrice('');
                setWeight('');
                setError('');
            }
        });
    };

    const handleClose = () => {
        setItems([]);
        setBarcode('');
        setFruitName('');
        setPrice('');
        setWeight('');
        setError('');
        onClose();
    };

    const totalAmount = items.reduce((sum, item) => sum + item.total, 0);

    // Check if selected date is today
    const today = new Date().toISOString().slice(0, 10);
    const isNotToday = date !== today;

    const handleDialogClose = (_event: any, reason: string) => {
        // Chỉ cho phép đóng bằng nút Hủy, không cho phép click backdrop hoặc ESC
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            maxWidth="lg"
            fullWidth
            disableEscapeKeyDown
            sx={{ p: 0, m: 1 }}
            id='phieu-thu-mua'
            PaperProps={{
                sx: {
                    height: 'calc(100vh - 16px)',
                    maxHeight: '100vh',
                }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h6" component="div">
                            📝 Phiếu Thu Mua
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Số phiếu: {purchaseNumber ?? 'Sẽ tạo tự động khi lưu'}
                        </Typography>
                    </Box>
                    <Box>
                        <DateOnlyPicker
                            value={date}
                            onChange={(newDate) => {
                                setDate(newDate);
                            }}
                            label="Ngày mua"
                            sx={{ width: '150px' }}
                        />
                    </Box>
                    {isNotToday && (
                        <Chip color="error"
                            sx={{ fontWeight: 'bold', height: '40px', ml: 1, width: 'fit-content' }}
                            label={`Không phải là ngày hôm nay (${new Date().toLocaleDateString('vi-VN')})`}
                            onClick={() => setDate(today)}
                        />
                    )}
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 0 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', fontSize: '20px', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 0 }}>
                                <Typography >
                                    👨🏻‍💼 {customerName}
                                </Typography>
                                <Typography>
                                    📞 {customerPhone}
                                </Typography>
                                <Typography>
                                    {customerType === 'Wholesale' ? '👨🏻‍💼 Thương lái' : '🙍‍♂️ Khách Lẻ'}
                                </Typography>
                            </Box>
                            <Box sx={{ mb: 0 }}>
                                <Typography>
                                    🏠 {customerAddress}
                                </Typography>
                            </Box>

                            <Card id="fruit-list" sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: '8px', height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                    {Object.entries(fruitTypes).map(([id, fruit]) => {
                                        const priceToShow = customerType === 'Wholesale' && fruit.wholesalePrice ? fruit.wholesalePrice : fruit.price;
                                        return (
                                            <Chip
                                                key={id}
                                                label={`${fruit.id}. ${fruit.name}\n${priceToShow.toLocaleString('vi-VN')}đ/kg`}
                                                onClick={() => {
                                                    handleBarcodeChange(id);
                                                    weightInputRef.current?.focus();
                                                }}
                                                color={barcode === id ? 'warning' : (customerType === 'Wholesale' ? 'info' : 'primary')}
                                                variant={'filled'}
                                                sx={{
                                                    cursor: 'pointer',
                                                    fontSize: '15px',
                                                    // fontWeight: barcode === id ? 'bold' : 'normal',
                                                    padding: '8px',
                                                    whiteSpace: 'normal',
                                                    height: 'auto',
                                                    lineHeight: 1.4,
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'scale(1.01)',
                                                        boxShadow: '0 4px 4px rgba(0,0,0,0.15)',
                                                    },
                                                    width: '48%',
                                                }}
                                            />
                                        );
                                    })}
                                    <Chip
                                        key={'add-new-fruit'}
                                        label={'Thêm loại mới'}
                                        onClick={handleStartAddFruit}
                                        color={'secondary'}
                                        variant={'filled'}
                                        sx={{
                                            cursor: 'pointer',
                                            fontSize: '18px',
                                            padding: '8px',
                                            whiteSpace: 'normal',
                                            height: 'auto',
                                            lineHeight: 1.4,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'scale(1.01)',
                                                boxShadow: '0 4px 4px rgba(0,0,0,0.15)',
                                            },
                                            width: '48%',
                                        }}
                                    />
                                </Box>
                            </Card>
                        </Box>
                        <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                            <Box sx={{ gap: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                                    <Box sx={{ fontWeight: 'bold', width: '200px' }}>
                                        📱 Quét Mã Vạch
                                    </Box>
                                    <TextField
                                        id="barcode-input"
                                        inputRef={barcodeInputRef}
                                        fullWidth
                                        size="small"
                                        placeholder="Nhập mã vạch..."
                                        value={barcode}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            handleBarcodeChange(e.target.value)
                                        }
                                        onKeyPress={(e: React.KeyboardEvent) => handleKeyPress(e, 'barcode')}
                                    />
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 2 }}>
                                        <Box sx={{ position: 'relative' }}>
                                            <Typography color="success" sx={{ fontWeight: 'bold', fontSize: '35px', height: '70px', textAlign: 'center', backgroundColor: '#98fc56', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {fruitName}
                                            </Typography>
                                            {barcode && fruitName && (
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 1)',
                                                        },
                                                    }}
                                                    onClick={() => {
                                                        setNewFruitId(barcode);
                                                        setNewFruitName(fruitName);
                                                        const fruit = fruitTypes[barcode];
                                                        setNewFruitPrice(fruit?.price?.toString() || '0');
                                                        setNewFruitWholesalePrice(fruit?.wholesalePrice?.toString() || '0');
                                                        setAddFruitOpen(true);
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                        <Box sx={{ position: 'relative' }}>
                                            <TextField
                                                id="price-input"
                                                fullWidth
                                                sx={{
                                                    '& .MuiInputBase-input': {
                                                        fontSize: 35,
                                                        textAlign: 'center',
                                                        fontWeight: 'bold',
                                                        color: '#2f32d3',
                                                    },
                                                }}
                                                size="small"
                                                label="Giá (VNĐ/kg)"
                                                type="number"
                                                placeholder="Giá tiền..."
                                                value={price}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    setPrice(e.target.value)
                                                }
                                                onKeyPress={(e: React.KeyboardEvent) => handleKeyPress(e, 'price')}
                                            />
                                            <Chip
                                                label={customerType === 'Wholesale' ? 'Giá sỉ' : 'Giá lẻ'}
                                                color={customerType === 'Wholesale' ? 'info' : 'primary'}
                                                size="small"
                                                sx={{ position: 'absolute', top: -10, right: 8 }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box sx={{ position: 'relative' }}>
                                        <TextField
                                            id="weight-input"
                                            inputRef={weightInputRef}
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    fontSize: 35,
                                                    paddingRight: weight ? '100px' : '16px',
                                                    lineHeight: 1.2,
                                                },
                                            }}
                                            multiline
                                            fullWidth
                                            size="small"
                                            label="Số lượng (kg)"
                                            placeholder="Số kg..."
                                            value={weight}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                setWeight(e.target.value)
                                            }
                                            onKeyPress={(e: React.KeyboardEvent) => handleKeyPress(e, 'weight')}
                                        />
                                        {weight && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    right: 12,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    fontSize: '35px',
                                                    fontWeight: 'bold',
                                                    color: '#2f32d3',
                                                    backgroundColor: '#f0f0f0',
                                                    borderRadius: '4px',
                                                    pointerEvents: 'none',
                                                }}
                                            >
                                                {parseWeightInput(weight.trim()).total}
                                            </Box>
                                        )}
                                    </Box>
                                    {/* <Button
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleAddItem(true)}
                                    >
                                        ➕ Thêm vào danh sách (Enter)
                                    </Button> */}
                                    {error && (
                                        <Typography color="error" variant="body2">
                                            {error}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                    {items.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                            Chưa có loại trái cây nào. Quét mã vạch để thêm.
                        </Box>
                    ) : (
                        <>
                            <TableContainer component={Paper} sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>STT</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Loại xoài</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Giá (VNĐ/kg)</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Số lượng (kg)</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Chi tiết</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Thành tiền</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Xóa</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.sort((a, b) => a.fruitTypeId > b.fruitTypeId ? 1 : -1).map((item, index) => (
                                            <TableRow key={item.id}>
                                                <TableCell sx={{ py: 0, textAlign: 'center' }}>{index + 1}</TableCell>
                                                <TableCell sx={{ py: 0 }}>{item.fruitTypeId}. {item.fruitTypeName}</TableCell>
                                                <TableCell align="right" sx={{ py: 0 }}>
                                                    {item.price.toLocaleString('vi-VN')}
                                                </TableCell>
                                                <TableCell sx={{ py: 0 }} align="right">{item.weight}</TableCell>
                                                <TableCell sx={{ py: 0 }}>
                                                    {item.details}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', py: 0 }} align="right">
                                                    {item.total.toLocaleString('vi-VN')}
                                                </TableCell>
                                                <TableCell sx={{ py: 0 }} align="center">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleRemoveItem(item.id)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={5} align="right">
                                                <Box component="span" sx={{ fontWeight: 'bold' }}>
                                                    Tổng cộng:
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                                    {totalAmount.toLocaleString('vi-VN')}
                                                </Box>
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Box>
            </DialogContent>
            <Dialog
                open={addFruitOpen}
                onClose={handleCloseAddFruit}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{newFruitId && fruitTypes[newFruitId] ? 'Chỉnh sửa loại trái cây' : 'Thêm loại trái cây mới'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Mã vạch"
                        value={newFruitId}
                        onChange={(e) => setNewFruitId(e.target.value)}
                        autoFocus
                        disabled={addFruitSaving || (newFruitId && fruitTypes[newFruitId] !== undefined) || false}
                    />
                    <TextField
                        label="Tên trái cây"
                        value={newFruitName}
                        onChange={(e) => setNewFruitName(e.target.value)}
                        disabled={addFruitSaving}
                    />
                    <TextField
                        label="Giá lẻ (VNĐ/kg)"
                        type="number"
                        value={newFruitPrice}
                        onChange={(e) => setNewFruitPrice(e.target.value)}
                        disabled={addFruitSaving}
                    />
                    <TextField
                        label="Giá sỉ (VNĐ/kg)"
                        type="number"
                        value={newFruitWholesalePrice}
                        onChange={(e) => setNewFruitWholesalePrice(e.target.value)}
                        disabled={addFruitSaving}
                        placeholder="0"
                    />
                    {addFruitError && (
                        <Alert severity="error">{addFruitError}</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddFruit} disabled={addFruitSaving}>Hủy</Button>
                    <Button
                        onClick={handleSubmitAddFruit}
                        variant="contained"
                        startIcon={addFruitSaving ? <CircularProgress size={20} color="inherit" /> : null}
                        disabled={addFruitSaving}
                    >
                        {addFruitSaving ? 'Đang lưu...' : 'Lưu'}
                    </Button>
                </DialogActions>
            </Dialog>
            <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3, pb: 2 }}>
                {saveError && (
                    <Alert severity="error" sx={{ width: '100%' }}>
                        {saveError}
                    </Alert>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={handleClose} disabled={saving}>Hủy</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="success"
                        disabled={items.length === 0 || saving}
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {saving ? 'Đang lưu...' : `💾 Lưu (${items.length} loại)`}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
